import React, { useEffect, useState } from "react";
import "./EnquiryList.scss";

import { utils, writeFile } from "xlsx";
import { DateRangePicker } from "rsuite";
import { Input, SelectPicker } from "rsuite";

import Button from "../../components/Button/Button";
import DataTable from "../../components/DataTable/DataTable";
import Loader from "../../components/Loader/Loader";
import { useSelector } from "react-redux";

// Icons
import {
  FaUsers,
  FaGraduationCap,
  FaSearch,
  FaFilter,
  FaDownload,
  FaSchool,
  FaCalendarAlt,
  FaHome,
  FaUtensils,
  FaBus,
  FaBed,
  FaExclamationTriangle,
  FaFileExcel,
  FaListAlt,
  FaChartBar,
  FaInfoCircle,
  FaDatabase,
  FaUserGraduate,
  FaBookOpen,
  FaClipboardList,
  FaTrophy,
  FaMedal,
  FaStar,
  FaQuestionCircle,
  FaPhoneAlt,
  FaMapMarkerAlt,
} from "react-icons/fa";

const BE_URL = process.env.REACT_APP_API_BASE_URL;

const EnquiryList = () => {
  //RBAC code block
  const allPermissions = useSelector((state) => state.permissions);
  const permissions = allPermissions
    .find((p) => p.name === "Admissions")
    ?.dropdowns.find((dd) => dd.name === "Enquiry List")?.permissions;

  const [isLoading, setIsLoading] = useState(false);
  const [enquiries, setEnquiries] = useState([]);
  const [filteredEnquiries, setFilteredEnquiries] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [sortOrder, setSortOrder] = useState("desc");
  const [dateRange, setDateRange] = useState([]);
  const [selectedClass, setSelectedClass] = useState("All Classes");
  const [selectedBranch, setSelectedBranch] = useState("Baramati");

  // Dropdown options
  const classOptions = [
    "All Classes",
    "Class 6th",
    "Class 7th",
    "Class 8th",
    "Class 9th",
    "Class 10th",
    "Class 11th",
    "Class 12th",
  ];

  const branches = ["Baramati", "Pune", "Nagar", "Ranchi", "All"];

  const branchData = branches.map((branch) => ({
    label: branch,
    value: branch,
  }));

  const classData = classOptions.map((cls) => ({
    label: cls,
    value: cls,
    role: cls === "All Classes" ? "All" : "Class",
  }));

  const handleClassFilterChange = (value) => {
    setSelectedClass(value);
  };

  const handleDateRangeChange = (item) => {
    if (!item) {
      setDateRange([]);
    } else {
      setDateRange(item);
    }
  };

  const fetchEnquiries = async () => {
    setIsLoading(true);
    try {
      const tokens = JSON.parse(localStorage.getItem("ERPTokens"));
      const response = await fetch(`${BE_URL}/getAllEnquiries`, {
        headers: {
          Authorization: `Bearer ${tokens.accessToken}`,
        },
      });

      if (!response.ok) {
        if (response.status === 403) {
          alert("Unauthorized Access");
        }
        if (response.status === 401) {
          const newTokens = await refreshToken();
          if (newTokens) {
            return fetchEnquiries();
          }
        }
        throw new Error("Failed to fetch enquiries");
      }

      const data = await response.json();
      setEnquiries(data);
      setFilteredEnquiries([]);
    } catch (error) {
      console.error("Error fetching data:", error);
    }
    setIsLoading(false);
  };

  const refreshToken = async () => {
    try {
      const tokens = JSON.parse(localStorage.getItem("ERPTokens"));
      const response = await fetch(`${BE_URL}/refreshToken`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ token: tokens.refreshToken }),
      });

      if (!response.ok) {
        throw new Error("Failed to refresh token");
      }

      const newTokens = await response.json();
      localStorage.setItem("ERPTokens", JSON.stringify(newTokens));
      return newTokens;
    } catch (error) {
      console.error("Error refreshing token:", error);
      alert("User logged out. Please login again");
      return null;
    }
  };

  useEffect(() => {
    fetchEnquiries();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [searchTerm, dateRange, selectedClass, selectedBranch, enquiries]);

  const applyFilters = () => {
    let filtered = enquiries.filter((enquiry) => {
      const enquiryDate = new Date(enquiry.timestamp);
      const [start, end] = dateRange;

      const searchTermLower = searchTerm.toLowerCase();
      const matchesSearchTerm = searchTerm
        ? enquiry.full_name.toLowerCase().includes(searchTermLower) ||
          enquiry.whatsapp.toLowerCase().includes(searchTermLower)
        : true;

      const matchesClass =
        selectedClass === "All Classes" ||
        enquiry.admission_for_class === selectedClass;

      const matchesBranch =
        selectedBranch === "All" || enquiry.branch === selectedBranch;

      const matchesDateRange =
        (!start || enquiryDate >= start) && (!end || enquiryDate <= end);

      return (
        matchesSearchTerm && matchesClass && matchesBranch && matchesDateRange
      );
    });

    setFilteredEnquiries(filtered);
  };

  useEffect(() => {
    setIsLoading(true);
    if (sortOrder === "desc") {
      setEnquiries((prevEnquiries) =>
        [...prevEnquiries].sort(
          (a, b) => new Date(b.timestamp) - new Date(a.timestamp)
        )
      );
    } else {
      setEnquiries((prevEnquiries) =>
        [...prevEnquiries].sort(
          (a, b) => new Date(a.timestamp) - new Date(b.timestamp)
        )
      );
    }
    setIsLoading(false);
  }, [sortOrder]);

  const downloadExcel = () => {
    setIsLoading(true);
    const ws = utils.json_to_sheet(filteredEnquiries);
    const wb = utils.book_new();
    utils.book_append_sheet(wb, ws, "Enquiries");
    writeFile(wb, "Enquiries.xlsx");
    setIsLoading(false);
  };

  // Calculate statistics
  const getStatistics = () => {
    const branchCounts = filteredEnquiries.reduce((acc, enquiry) => {
      acc[enquiry.branch] = (acc[enquiry.branch] || 0) + 1;
      return acc;
    }, {});

    const classCounts = filteredEnquiries.reduce((acc, enquiry) => {
      const className = enquiry.admission_for_class;
      acc[className] = (acc[className] || 0) + 1;
      return acc;
    }, {});

    const topBranch = Object.keys(branchCounts).reduce(
      (a, b) => (branchCounts[a] > branchCounts[b] ? a : b),
      "N/A"
    );

    const topClass = Object.keys(classCounts).reduce(
      (a, b) => (classCounts[a] > classCounts[b] ? a : b),
      "N/A"
    );

    const recentEnquiries = filteredEnquiries.filter((enquiry) => {
      const enquiryDate = new Date(enquiry.timestamp);
      const oneWeekAgo = new Date();
      oneWeekAgo.setDate(oneWeekAgo.getDate() - 7);
      return enquiryDate >= oneWeekAgo;
    }).length;

    return {
      total: filteredEnquiries.length,
      topBranch: topBranch,
      topBranchCount: branchCounts[topBranch] || 0,
      topClass: topClass,
      topClassCount: classCounts[topClass] || 0,
      recent: recentEnquiries,
    };
  };

  const stats = getStatistics();

  return (
    <div className="enquiry-list-container">
      {permissions?.can_read ? (
        <>
          {/* Header Section */}
          <div className="enquiry-list-header">
            <div className="enquiry-list-header-left">
              <div className="enquiry-list-header-icon">
                <FaQuestionCircle />
              </div>
              <div className="enquiry-list-header-content">
                <h1 className="enquiry-list-title">Enquiry List Management</h1>
                <p className="enquiry-list-subtitle">
                  Comprehensive enquiry tracking and analysis
                </p>
              </div>
            </div>
            <div className="enquiry-list-header-right">
              <div className="enquiry-list-actions">
                <button
                  className="action-btn excel-btn"
                  onClick={downloadExcel}
                  disabled={filteredEnquiries.length === 0}
                >
                  <FaFileExcel />
                  <span>Export Excel</span>
                </button>
              </div>
            </div>
          </div>

          {/* Statistics Cards */}
          <div className="enquiry-list-stats">
            <div className="stat-card primary">
              <div className="stat-icon">
                <FaUsers />
              </div>
              <div className="stat-content">
                <h3 className="stat-number">{stats.total}</h3>
                <p className="stat-label">Total Enquiries</p>
              </div>
            </div>
            <div className="stat-card recent">
              <div className="stat-icon">
                <FaCalendarAlt />
              </div>
              <div className="stat-content">
                <h3 className="stat-number">{stats.recent}</h3>
                <p className="stat-label">Recent (7 Days)</p>
              </div>
            </div>
            <div className="stat-card branch">
              <div className="stat-icon">
                <FaMapMarkerAlt />
              </div>
              <div className="stat-content">
                <h3 className="stat-number">{stats.topBranchCount}</h3>
                <p className="stat-label">Top Branch ({stats.topBranch})</p>
              </div>
            </div>
            <div className="stat-card class">
              <div className="stat-icon">
                <FaGraduationCap />
              </div>
              <div className="stat-content">
                <h3 className="stat-number">{stats.topClassCount}</h3>
                <p className="stat-label">Top Class ({stats.topClass})</p>
              </div>
            </div>
          </div>

          {/* Filters Section */}
          <div className="enquiry-list-filters">
            <div className="filters-header">
              <div className="filters-header-left">
                <FaFilter />
                <h2>Filters & Search</h2>
              </div>
              <div className="filters-header-right">
                <span className="results-count">
                  {filteredEnquiries.length} of {enquiries.length} enquiries
                </span>
              </div>
            </div>

            <div className="filters-grid">
              {/* Search Filter */}
              <div className="filter-group search-group">
                <label className="filter-label">
                  <FaSearch />
                  <span>Search Enquiries</span>
                </label>
                <div className="search-input-wrapper">
                  <FaSearch className="search-icon" />
                  <Input
                    placeholder="Search by name or WhatsApp number..."
                    onChange={(value) => setSearchTerm(value)}
                    className="search-input"
                  />
                </div>
              </div>

              {/* Date Range Filter */}
              <div className="filter-group">
                <label className="filter-label">
                  <FaCalendarAlt />
                  <span>Date Range</span>
                </label>
                <DateRangePicker
                  format="yyyy-MM-dd"
                  onChange={handleDateRangeChange}
                  value={dateRange}
                  className="filter-select"
                />
              </div>

              {/* Branch Filter */}
              <div className="filter-group">
                <label className="filter-label">
                  <FaMapMarkerAlt />
                  <span>Branch</span>
                </label>
                <SelectPicker
                  data={branchData}
                  onChange={(e) => setSelectedBranch(e)}
                  value={selectedBranch}
                  cleanable={false}
                  searchable={false}
                  placeholder="Select Branch"
                  className="filter-select"
                />
              </div>

              {/* Class Filter */}
              <div className="filter-group">
                <label className="filter-label">
                  <FaGraduationCap />
                  <span>Admission Class</span>
                </label>
                <SelectPicker
                  data={classData}
                  onChange={handleClassFilterChange}
                  value={selectedClass}
                  cleanable={false}
                  searchable={false}
                  placeholder="Select Class"
                  className="filter-select"
                />
              </div>
            </div>
          </div>

          {/* Enquiry Table */}
          <div className="enquiry-list-table">
            {isLoading ? (
              <div className="loading-container">
                <Loader />
                <p>Loading enquiries...</p>
              </div>
            ) : (
              <>
                <div className="enquiry-table-header">
                  <div className="header-id">Id</div>
                  <div className="header-name">Name</div>
                  <div className="header-whatsapp">WhatsApp</div>
                  <div className="header-class">Admission in Class</div>
                  <div
                    className="header-timestamp"
                    onClick={() =>
                      setSortOrder(sortOrder === "desc" ? "asc" : "desc")
                    }
                  >
                    TimeStamp {sortOrder === "desc" ? " ↑" : " ↓"}
                  </div>
                </div>
                <DataTable
                  data={filteredEnquiries}
                  navigateBaseUrl={`/admin/enquiry`}
                  setSortOrder={setSortOrder}
                  sortOrder={sortOrder}
                />
                <div className="enquiry-table-footer">
                  {filteredEnquiries.length} items found.
                </div>
              </>
            )}
          </div>
        </>
      ) : (
        <div className="no-permission">
          <FaExclamationTriangle />
          <h2>Access Denied</h2>
          <p>You don't have permission to access this section.</p>
        </div>
      )}
    </div>
  );
};

export default EnquiryList;
